import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectMaintenances } from "../../store/slices/maintenances";
import {
  selectStudentTickets,
  selectStudentTicketsExport,
  tryStudentTickets,
  tryStudentTicketsExport,
  tryStudentTicketsSummary,
} from "../../store/slices/student-tickets";
import {
  Button,
  Grid,
  Typography,
  TextField,
  Card,
  CardActionArea,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogActions,
  ListItemButton,
  List,
  ListItemText,
  Box,
  ListItemIcon,
  Divider,
  Pagination,
  Stack,
  IconButton,
} from "@mui/material";
import {
  MarkEmailRead,
  MarkEmailUnread,
  Search,
  Add,
  Inbox,
  Clear,
  Download,
  MailLock,
} from "@mui/icons-material";
import AddStudentTicketDrawer from "./drawers/AddStudentTicketDrawer";
import StudentTicketsDrawer from "./drawers/StudentTicketsDrawer";
import ModalMessages from "../layout/modal-messages";
import StudentTicketMainSkeleton from "./skeleton";
import messageCutter from "../../services/functions/messageCutter";
import Spinner from "../layout/spinner";
import filterByMessageSent from "../../services/functions/filterByMessageSent";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const StudentTickets = ({ clientId, businessId, roleId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const typeTables = useSelector(selectMaintenances);
  const studentTicketsExport = useSelector(selectStudentTicketsExport);
  const studentTickets = useSelector(selectStudentTickets);

  const { isLoading, errors, data: tickets, totalPages } = studentTickets;

  let [ticketsFiltered, setTicketsFiltered] = useState(tickets);

  //Dialog de confirmación
  const [dialog, setDialog] = useState(false);

  //MANEJO DE DRAWER Y SET DE LA INFO QUE SE LE PASA
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [openTicketDrawer, setOpenTicketDrawer] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [hasTopic, setHasTopic] = useState(false);
  const [topicSelected, setTopicSelected] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [showPagination, setShowPagination] = useState(true);
  const [page, setPage] = useState(1);

  const fetchData = async () => {
    await dispatch(
      tryStudentTickets({ studentId: businessId, pageNumber: page })
    );
  };

  const handleCloseDialog = async () => {
    setOpenDialog(false);
    fetchData();
  };

  const filterTickets = (value) => {
    setShowPagination(false);
    if (value) {
      const ticketsFilter = tickets.filter((ticket) => {
        return filterByMessageSent(ticket.messageSent, value);
      });
      setTicketsFiltered(ticketsFilter);
    }

    if (!value) {
      setTicketsFiltered(tickets);
      setShowPagination(true);
    }
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    fetchData();
    setHasTopic(false);
    setTopicSelected(null);
  }, [page]);

  useEffect(() => {
    setTicketsFiltered(tickets);
  }, [tickets]);

  useEffect(() => {
    if (!openTicketDrawer || !openAddDrawer)
      dispatch(tryStudentTicketsSummary(businessId));
  }, [openTicketDrawer, openAddDrawer]);

  const handleOpenTicket = (ticket) => {
    setOpenTicketDrawer(true);
    setTicketData(ticket);
  };

  const RenderTickets = () => {
    return (
      <>
        {ticketsFiltered.map((ticket) => {
          return (
            <Card
              key={ticket.ticketId}
              sx={{ backgroundColor: "rgb(231, 235, 240)", marginBottom: 1 }}
              onClick={() => handleOpenTicket(ticket)}
            >
              <CardActionArea>
                <CardContent>
                  <Grid container pt={2} direction="row" alignItems="center">
                    <Grid item xs={2} sm={1} sx={{ color: "#22577A", textAlign: "center" }}>
                      {ticket.statusOfMessageId === 2 && <MarkEmailRead />}
                      {ticket.statusOfMessageId === 1 && <MarkEmailUnread />}
                      {ticket.statusOfMessageId === 3 && <MailLock />}
                    </Grid>
                    <Grid item xs={10} sm={11} rowSpacing={2}>
                      <Typography
                        fontSize={15}
                        fontWeight={700}
                        sx={{ color: "#22577A" }}
                      >
                        {ticket.topic}
                      </Typography>
                      <Typography sx={{ fontSize: 14 }}>
                        {ticket.subTopic}
                      </Typography>
                      <Typography sx={{ fontSize: 12 }}>
                        {messageCutter(ticket.titleOfTicket)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
        {showPagination && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              color="primary"
              defaultPage={page}
              page={page}
              size={isSmallMobile ? "small" : "medium"}
              onChange={(e, value) => handleChangePage(e, value)}
            />
          </Box>
        )}
      </>
    );
  };

  const RenderTopics = () => {
    return (
      <>
        <List component="nav" aria-label="secondary mailbox folder">
          {typeTables.TBLTOPICS.data.map((topic) => {
            return (
              <ListItemButton
                sx={
                  hasTopic && topicSelected === topic.description
                    ? {
                      color: "white",
                      backgroundColor: "#22577A",
                      ":hover": {
                        color: "white",
                        backgroundColor: "#22577A",
                      },
                    }
                    : { color: "black" }
                }
                key={topic.id}
                onClick={() => handleFilter(topic.description)}
              >
                <ListItemText primary={topic.description} />
              </ListItemButton>
            );
          })}
        </List>
      </>
    );
  };

  const handleFilter = (description) => {
    setShowPagination(false);
    setTopicSelected(description);
    const ticketsFilter = tickets.filter((ticket) => {
      return ticket.topic === description;
    });
    setTicketsFiltered(ticketsFilter);
    setHasTopic(true);
  };

  const handleClearFilter = () => {
    setShowPagination(true);
    setTicketsFiltered(tickets);
    setHasTopic(false);
  };

  const handleClickExport = async () => {
    const action = await dispatch(tryStudentTicketsExport(businessId));

    if (action.payload.data.isValid) window.open(action.payload.data.data.file);
  };

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {isLoading && <StudentTicketMainSkeleton />}
      <Grid container spacing={2} pt={2} rowSpacing={2} direction="row">
        <Grid item xs={12}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="flex-end"
            spacing={2}
            sx={{ pr: { xs: 0, sm: 2 } }}
          >
            <Button
              variant="contained"
              endIcon={<Download />}
              onClick={() => handleClickExport()}
              disabled={studentTicketsExport.isLoading}
              fullWidth={isSmallMobile}
            >
              {studentTicketsExport.isLoading && <Spinner color="primary" />}
              Exportar
            </Button>
            {roleId !== 1 && (
              <Button
                variant="contained"
                endIcon={<Add />}
                onClick={() => {
                  setOpenAddDrawer(true);
                }}
                fullWidth={isSmallMobile}
              >
                NUEVO MENSAJE
              </Button>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            type="search"
            label="Buscar sobre el título del mensaje"
            onChange={(event) => {
              filterTickets(event.target.value);
            }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{ width: "100%", maxWidth: { xs: "100%", md: 360 }, bgcolor: "background.paper", border: "1px solid #e0e0e0", borderRadius: 1 }}
          >
            <List component="nav" aria-label="main mailbox folders">
              <ListItemButton selected={true}>
                <ListItemIcon>
                  <Inbox />
                </ListItemIcon>
                <ListItemText primary="Tópicos" />
                {hasTopic && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography fontSize={12} mr={1}> Limpiar filtro</Typography>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClearFilter(); }}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </ListItemButton>
            </List>
            <Divider />
            <RenderTopics />
          </Box>
        </Grid>
        <Grid item xs={12} md={8} sx={{ pb: 2 }}>
          <RenderTickets />
        </Grid>
      </Grid>

      {openAddDrawer && (
        <AddStudentTicketDrawer
          openDrawer={openAddDrawer}
          setOpenDrawer={setOpenAddDrawer}
          setOpenDialog={setOpenDialog}
          businessId={businessId}
          clientId={clientId}
        />
      )}
      {openTicketDrawer && (
        <StudentTicketsDrawer
          openTicketDrawer={openTicketDrawer}
          setOpenTicketDrawer={setOpenTicketDrawer}
          ticketData={ticketData}
          clientId={clientId}
          businessId={businessId}
          roleId={roleId}
          setDialog={setDialog}
        />
      )}
      {dialog && (
        <Dialog
          open={dialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"La información ha sido guardada con éxito"}
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={() => {
                fetchData();
                setDialog(false);
              }}
              autoFocus
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <>
        <Dialog
          open={openDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Se ha enviado con éxito"}
          </DialogTitle>
          <DialogActions>
            <Button onClick={() => handleCloseDialog()} autoFocus>
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
      </>
      {errors && errors.length > 0 && <ModalMessages errors={errors} />}
    </Box>
  );
};

export default StudentTickets;